<template>
  <ToolShell
    tool-name="Invoice due date calculator"
    h1="Invoice due date calculator for clear payment terms."
    lede="An invoice's due date is the invoice date plus the days in your payment terms, so net 30 from October 24 is due November 23. Pick the invoice date and terms to get the exact due date, a simple reminder schedule, and the first day the invoice counts as late. Free, instant, no signup."
    :trust="['No signup', 'Instant results', 'Reminder schedule included']"
    form-title="Your invoice"
    :upsell-points="[
      'The terms and due date print on every invoice you send',
      'Reminder emails and SMS send as the date approaches',
      'You see what’s due, late, and paid on one list',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'The due date and reminder schedule for one invoice',
      'You send each reminder yourself',
      'Paid status lives in your bank app, not next to the invoice',
    ]"
    upgrade-headline="This date works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Terms and due dates print on every invoice automatically',
      'Reminder emails and SMS send on a schedule with automations',
      'Payment links collect online',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/invoice-due-date-calculator"
    :meta-description="metaDescription"
    doc-brand="Due date calculator"
    :doc-subtitle="termsSummary"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Invoice date</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model="form.date" type="date" />
        </div>
      </label>

      <div class="ta-field">
        <span id="duedate-terms-label">Payment terms</span>
        <ToolDropdown
          v-model="form.terms"
          :options="termsOptions"
          aria-label="Payment terms"
          labelledby="duedate-terms-label"
        />
      </div>

      <!-- Hidden in /embed/tools/: plan claims and in-frame site links stay off other sites. -->
      <p v-if="!embed" class="ta-note">
        Terms print on every invoice, and reminder emails (and texts from Voyager) send as the date approaches —
        email automations and due-date tracking are on every plan from $29 (up to 3 active workflows),
        and SMS steps start on Voyager ($74/month). Read
        <NuxtLink :to="localePath('/learn/net-30-payment-terms')">net 30 payment terms</NuxtLink>,
        or work out a fee with the
        <NuxtLink :to="localePath('/tools/late-fee-calculator')">late fee calculator</NuxtLink>.
      </p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Invoice date</span><strong>{{ fmtLong(issueDate) }}</strong></div>
        <div><span>Terms</span><strong>{{ termsLabel }}</strong></div>
        <div><span>Days from issue</span><strong>{{ daysFromIssue === null ? "—" : `${daysFromIssue} days` }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Schedule</th><th>Date</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Send the invoice</td>
              <td class="ta-num">{{ fmtShort(issueDate) }}</td>
            </tr>
            <tr>
              <td>Halfway reminder</td>
              <td class="ta-num">{{ fmtShort(halfwayDate) }}</td>
            </tr>
            <tr>
              <td>Payment due</td>
              <td class="ta-num">{{ fmtShort(dueDate) }}</td>
            </tr>
            <tr>
              <td>First late day</td>
              <td class="ta-num">{{ fmtShort(firstLateDate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total">
        <span>Payment due</span>
        <strong>{{ fmtLong(dueDate) }}</strong>
      </div>

      <p class="ta-doc-note">Shorter terms win when the client is a person, the job is small, or they pay by card — the money arrives before the memory fades. Longer terms suit businesses that pay on a monthly run.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Pick the invoice date</h2><p>The date you send — or plan to send — the invoice.</p></div></div>
      <div><span>02</span><div><h2>Choose the terms</h2><p>Due on receipt, net 7 to net 60, or end of month plus 15 days.</p></div></div>
      <div><span>03</span><div><h2>Read the schedule</h2><p>The due date, halfway reminder, and first late day update live as you change the terms.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Due date questions" :faq="faq" />

    <RelatedPages path="/tools/invoice-due-date-calculator" title="Invoice due date calculator" />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const { localePath } = useWorkspaceLocale();
const embed = inject("toolEmbed", false);

const form = reactive({ date: "", terms: 30 });

// Dates are client-only: prerendered HTML must stay identical day to day or
// the localization pipeline fails with "Rendered site copy changed".
onMounted(() => {
  if (!form.date) form.date = new Date().toISOString().slice(0, 10);
});

const termsOptions = [
  { value: 0, label: "Due on receipt", hint: "same day" },
  { value: 7, label: "Net 7", hint: "+7 days" },
  { value: 14, label: "Net 14", hint: "+14 days" },
  { value: 30, label: "Net 30", hint: "+30 days" },
  { value: 45, label: "Net 45", hint: "+45 days" },
  { value: 60, label: "Net 60", hint: "+60 days" },
  { value: "eom15", label: "End of month + 15", hint: "15th of next month" },
];

const DAY_MS = 24 * 60 * 60 * 1000;

const issueDate = computed(() => (form.date ? new Date(`${form.date}T00:00:00`) : null));

const dueDate = computed(() => {
  if (!issueDate.value) return null;
  if (form.terms === "eom15") {
    return new Date(issueDate.value.getFullYear(), issueDate.value.getMonth() + 1, 15);
  }
  const due = new Date(issueDate.value);
  due.setDate(due.getDate() + Number(form.terms));
  return due;
});

const daysFromIssue = computed(() =>
  issueDate.value && dueDate.value
    ? Math.round((dueDate.value.getTime() - issueDate.value.getTime()) / DAY_MS)
    : null,
);

const halfwayDate = computed(() => {
  if (!issueDate.value || daysFromIssue.value === null) return null;
  const halfway = new Date(issueDate.value);
  halfway.setDate(halfway.getDate() + Math.floor(daysFromIssue.value / 2));
  return halfway;
});

const firstLateDate = computed(() => {
  if (!dueDate.value) return null;
  const late = new Date(dueDate.value);
  late.setDate(late.getDate() + 1);
  return late;
});

const termsLabel = computed(
  () => termsOptions.find((option) => option.value === form.terms)?.label || "",
);

const fmtLong = (date) =>
  date
    ? date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "—";
const fmtShort = (date) =>
  date
    ? date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

const termsSummary = computed(() =>
  issueDate.value ? `${termsLabel.value} · issued ${fmtShort(issueDate.value)}` : termsLabel.value,
);

const guideSections = [
  {
    title: "How the due date is counted",
    paragraphs: [
      "Net terms count calendar days from the invoice date, weekends and holidays included. The invoice date is day zero, so net 30 on an invoice dated October 24 means payment is due on November 23.",
    ],
    bullets: [
      "Due on receipt: due the same day the invoice is issued.",
      "Net 7 to net 60: the invoice date plus that many days.",
      "End of month + 15: the 15th of the month after the invoice date, however late in the month you send it.",
      "Halfway reminder: half the days between the invoice date and the due date, rounded down.",
      "First late day: the day after the due date. Until then the invoice is on time.",
    ],
  },
  {
    title: "Worked examples from one invoice date",
    paragraphs: [
      "Here is what the calculator shows for an invoice dated Saturday, October 24, 2026.",
    ],
    bullets: [
      "Net 14: due Saturday, November 7, with a halfway reminder on October 31 and the first late day on November 8.",
      "Net 30: due Monday, November 23, with a halfway reminder on November 8 and the first late day on November 24.",
      "Net 60: due Wednesday, December 23, with a halfway reminder on November 23 and the first late day on December 24.",
      "End of month + 15: due Sunday, November 15, which is 22 days away. The same terms on an invoice dated October 1 give the client 45 days.",
    ],
  },
  {
    title: "When the due date falls on a weekend",
    paragraphs: [
      "The calculator does not move a due date that lands on a Saturday, Sunday or public holiday, because terms are counted in calendar days. Some clients will pay on their next working day anyway. If that matters to you, say in your terms whether a weekend due date moves to the next business day, and treat the first late day the same way before you send a reminder or add a fee.",
    ],
  },
  {
    title: "Picking terms by client",
    paragraphs: [
      "The right terms are the ones the client agreed to before the work started, written into the proposal and repeated on the invoice.",
    ],
    bullets: [
      "Individuals and one-off jobs: due on receipt or net 7, ideally with a card payment link.",
      "Small businesses and repeat clients: net 14, or net 30 where that is their normal cycle.",
      "Larger companies with a monthly payment run: net 30, net 45 or end of month + 15, matched to when their run happens.",
      "Ongoing work: bill on a fixed date each month and keep the terms short, so one invoice is paid before the next goes out.",
    ],
    links: [
      { label: "Net 30 payment terms explained", path: "/learn/net-30-payment-terms" },
      { label: "How to get paid faster", path: "/learn/how-to-get-paid-faster" },
    ],
  },
  {
    title: "Due dates and reminders in Workspace369",
    paragraphs: [
      "Workspace369 lets you set payment terms and a default due date for your invoices, and late-payment reminders go out by email on every plan, with text reminders on plans that include texting (Voyager and up). Automatic late fees, with a grace period of days after the due date, start on Commander.",
    ],
    links: [
      { label: "Payment reminder generator", path: "/tools/payment-reminder-generator" },
      { label: "Late fee calculator", path: "/tools/late-fee-calculator" },
    ],
  },
];

const faq = [
  {
    q: "How do I calculate an invoice due date?",
    a: "Add the number of days in your payment terms to the invoice date, counting calendar days including weekends. An invoice dated October 24 on net 30 is due November 23. For end of month + 15, the due date is the 15th of the following month.",
  },
  {
    q: "Does net 30 include weekends?",
    a: "Yes. Net terms count calendar days, not business days, unless your agreement says otherwise. If the due date lands on a weekend or holiday, your terms should say whether payment can wait until the next business day.",
  },
  {
    q: "When does an invoice become overdue?",
    a: "On the day after the due date. An invoice due on November 23 is on time if paid that day and overdue from November 24.",
  },
  {
    q: "Should the due date be on the invoice?",
    a: "Yes. Print both the terms and the exact date, such as Net 30, due November 23, 2026. The client's accounts team should not have to work it out.",
  },
];

const metaTitle = "Invoice Due Date Calculator | Workspace369";
const metaDescription =
  "Free invoice due date calculator: pick the invoice date and payment terms to get the exact due date, a reminder schedule, and the first late day.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
}));
</script>
